import { siteText } from '../siteText'
import { api } from '../api'
import { useAsync } from '../hooks'
import type { Job } from '../types'
import './SourcePipeline.css'

const measuredCount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0
const plural = (count: number, singular: string, multiple = `${singular}s`) => `${count} ${count === 1 ? singular : multiple}`

function describeJob(job: Job) {
  const state = job.pipeline
  const counts = state && typeof state.completed === 'object' ? state.completed : null
  const total = measuredCount(state?.total) && state.total > 0 ? state.total : null
  const completed = counts ? counts.reconciliation : state?.completed
  const measured = total !== null && measuredCount(completed)
  const reviewed = measured ? Math.min(completed, total) : null
  const queued = job.status === 'queued'
  let title = job.kind === 'ingest' ? 'Preparing transcription' : job.kind === 'render' ? 'Preparing previews' : 'Finding clips'
  let detail = ''
  if (!queued && job.kind === 'ingest' && counts) {
    title = total !== null && counts.whisper >= total ? 'Checking word timings' : 'Transcribing recording'
    if (reviewed !== null) detail = `${reviewed} of ${total} segments timed`
  } else if (!queued && job.kind === 'analyze' && state?.stage === 'analysis') {
    title = 'Reviewing segments'
    if (reviewed !== null) detail = `${reviewed} of ${total} segments reviewed`
  } else if (!queued && job.message) {
    title = job.message
  }
  const substeps = counts && total !== null ? [
    measuredCount(counts.diarization) ? `Speakers identified in ${Math.min(counts.diarization, total)} of ${total}` : '',
    measuredCount(counts.whisper) ? `Words transcribed in ${Math.min(counts.whisper, total)} of ${total}` : '',
  ].filter(Boolean) : []
  // A window count measures only this phase, never the whole recording workflow.
  const phaseProgress = !queued && reviewed !== null && (counts || state?.stage === 'analysis') ? reviewed / total! : null
  return { title, detail, substeps, phaseProgress, reviewed, total }
}

export function PipelineStatus({ jobs }: { jobs: Job[] }) {
  const active = jobs.filter((job) => ['queued', 'running'].includes(job.status) && ['ingest', 'analyze'].includes(job.kind))
  const summary = jobs.find(job => measuredCount(job.pipeline?.candidates_found) || measuredCount(job.pipeline?.previews_ready) || measuredCount(job.pipeline?.previews_processing))?.pipeline
  // Source jobs omit render work. The server's current source summary keeps
  // this visible after discovery while actual preview jobs remain unfinished.
  const preparingPreviews = !active.length && measuredCount(summary?.previews_processing) && summary.previews_processing > 0
  if (!active.length && !preparingPreviews) return null
  const summaryParts = [
    measuredCount(summary?.candidates_found) ? `${plural(summary.candidates_found, 'candidate')} found` : '',
    measuredCount(summary?.previews_ready) ? `${plural(summary.previews_ready, 'preview')} ready` : '',
    measuredCount(summary?.previews_processing) ? `${plural(summary.previews_processing, 'preview')} processing` : '',
  ].filter(Boolean)
  return <div className="source-pipeline" role="status" aria-live="polite">
    <span className="motion-logo workspace-loading-logo" aria-hidden="true" />
    <div className="source-pipeline-content">{active.map((job) => {
      const { title, detail, substeps, phaseProgress, reviewed, total } = describeJob(job)
      return <div className="source-pipeline-stage" key={job.id}>
        <div className="source-pipeline-heading"><span>{siteText(title)}</span>{detail ? <small>{siteText(detail)}</small> : null}</div>
        {phaseProgress !== null ? <div className="source-pipeline-track" role="progressbar" aria-label={siteText(title)} aria-valuemin={0} aria-valuemax={total!} aria-valuenow={reviewed!} aria-valuetext={siteText(detail)}><span style={{ width: `${phaseProgress * 100}%` }} /></div> : null}
        {substeps.length ? <small className="source-pipeline-substeps">{siteText(substeps.join(' · '))}</small> : null}
      </div>
    })}
    {preparingPreviews ? <div className="source-pipeline-stage"><div className="source-pipeline-heading"><span>Preparing previews</span></div></div> : null}
    {summaryParts.length ? <div className="source-pipeline-summary"><span>This recording</span><small>{siteText(summaryParts.join(' · '))}</small></div> : null}
    </div>
  </div>
}

export function SourcePipeline({ sourceId }: { sourceId: string }) {
  const state = useAsync(() => api.jobs(sourceId), [sourceId], { pollMs: 2500 })
  return <PipelineStatus jobs={(state.data || []).filter((job) => job.source_id === sourceId)} />
}
